import React, { useEffect, useState } from 'react';
import { Modal } from '@appkit4/react-components/modal';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import './PdfTemplateModal.scss';
import { useGetV1DocumentTypeDocumentTypeId } from '../../../api/generated/endpoint';
import { DocumentType } from '../../../api/generated/models';
import { Loading } from '@appkit4/react-components';

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

interface PDFTemplateModalProps {
    visible: boolean;
    onClose: () => void;
    documentTypeId: number;
}

const PDFTemplateModal: React.FC<PDFTemplateModalProps> = ({
    visible,
    onClose,
    documentTypeId,
}) => {
    const [numPages, setNumPages] = useState<number>(0);
    const [pageNumber, setPageNumber] = useState<number>(1);
    const [scale, setScale] = useState<number>(1.3);
    const [pdfUrl, setPdfUrl] = useState<string>('');

    const { data, isLoading } =
        useGetV1DocumentTypeDocumentTypeId<DocumentType>(documentTypeId);

    useEffect(() => {
        if (data) {
            setPdfUrl(data.fillPdfTemplateBlobUri ?? '');
        }
    }, [data]);

    function onDocumentLoadSuccess({ numPages }: { numPages: number }): void {
        setNumPages(numPages);
    }

    const zoomIn = () => {
        setScale((prevScale) => Math.min(prevScale + 0.1, 2.0));
    };

    const zoomOut = () => {
        setScale((prevScale) => Math.max(prevScale - 0.1, 0.5));
    };

    const goToNextPage = () => {
        setPageNumber((prevPage) => Math.min(prevPage + 1, numPages));
    };

    const goToPrevPage = () => {
        setPageNumber((prevPage) => Math.max(prevPage - 1, 1));
    };

    const onPageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const page = parseInt(e.target.value);
        if (page >= 1 && page <= numPages) {
            setPageNumber(page);
        }
    };

    return (
        <Modal
            visible={visible}
            onCancel={onClose}
            footer={null}
            closable={true}
            className="pdf-viewer-modal"
            modalStyle={{ width: '80vw', height: '90vh' }}
        >
            {isLoading ? (
                <Loading loadingType="circular" indeterminate={true} />
            ) : (
                <div className="pdf-container">
                    <div className="pdf-controls">
                        <div className="zoom-control">
                            <span>Zoom</span>
                            <button className="zoom-button" onClick={zoomOut}>
                                -
                            </button>
                            <span>{Math.round(scale * 100)}%</span>
                            <button className="zoom-button" onClick={zoomIn}>
                                +
                            </button>
                        </div>
                        <div className="page-control">
                            <span>Page</span>
                            <input
                                type="number"
                                min={1}
                                max={numPages}
                                value={pageNumber}
                                onChange={onPageChange}
                                style={{ width: '100px' }}
                            />
                            <span>of {numPages}</span>
                            <button
                                className="zoom-button"
                                onClick={goToPrevPage}
                            >
                                &lt;
                            </button>
                            <button
                                className="zoom-button"
                                onClick={goToNextPage}
                            >
                                &gt;
                            </button>
                        </div>
                    </div>
                    <Document
                        file={pdfUrl}
                        onLoadSuccess={onDocumentLoadSuccess}
                        loading={
                            <Loading
                                loadingType="circular"
                                indeterminate={true}
                            />
                        }
                    >
                        <Page
                            pageNumber={pageNumber}
                            scale={scale}
                            renderAnnotationLayer={true}
                            renderTextLayer={true}
                        />
                    </Document>
                </div>
            )}
        </Modal>
    );
};

export default PDFTemplateModal;
